import {
  ItemTypeEnum,
  ProductTypeEnum,
  abutmentCharacteristicsSchema,
  implantCharacteristicsSchema,
  implantCrownSchema,
  implantedItemCharacteristicsSchema,
  materialCharacteristicsSchema,
  productPreferencesSchema,
  type AbutmentItem,
  type DeepPartial,
  type ImplantCrownItem,
  type ImplantCrownProduct,
} from "@jasper/client-models";
import {
  isTeethStepComplete,
  TeethSelectionEnum,
  type ProductWorkflowConfiguration,
} from "./ProductWorkflowConfiguration";
import { StepKeysEnum } from "../../steps/types";
import {
  AbutmentStep,
  CrownTeethStep,
  ImplantConnectionStep,
  ImplantRetentionStep,
  MaterialStep,
  PreferencesStep,
} from "./constants";

export type ImplantCrownStepKey =
  | typeof StepKeysEnum.crownTeeth
  | typeof StepKeysEnum.implantConnection
  | typeof StepKeysEnum.abutment
  | typeof StepKeysEnum.implantRetention
  | typeof StepKeysEnum.material
  | typeof StepKeysEnum.preferences;

const isAbutment = (
  item: DeepPartial<ImplantCrownItem | AbutmentItem> = {}
): item is DeepPartial<AbutmentItem> =>
  item.type === ItemTypeEnum.abutment ||
  item.type === ItemTypeEnum.customAbutment;

const isImplantCrown = (
  item: DeepPartial<ImplantCrownItem | AbutmentItem> = {}
): item is DeepPartial<ImplantCrownItem> =>
  item.type === ItemTypeEnum.implantCrown;

export const ImplantCrownWorkflowConfiguration: ProductWorkflowConfiguration<
  ImplantCrownProduct,
  ImplantCrownStepKey
> = {
  productType: ProductTypeEnum.implantCrown,
  itemType: ItemTypeEnum.implantCrown,
  steps: [
    new CrownTeethStep({ type: "IMPLANT" }),
    new ImplantConnectionStep(),
    new AbutmentStep(),
    new ImplantRetentionStep(),
    new MaterialStep({
      props: {
        materialItemTypes: [ItemTypeEnum.implantCrown],
        materialProductType: ProductTypeEnum.implantCrown,
      },
    }),
    new PreferencesStep(),
  ],
  teethSelection: TeethSelectionEnum.MIXED,
  isBulkSupported: true,
  isBulkCompatible: (_leaderProduct, followerProduct) =>
    followerProduct.type === ProductTypeEnum.implantCrown ||
    followerProduct.type === ProductTypeEnum.splintedCrownImplant,

  isValidProduct(product) {
    return implantCrownSchema.safeParse(product).success;
  },

  getCompletedSteps(product) {
    const completed: ImplantCrownStepKey[] = [];

    if (
      isTeethStepComplete({
        teeth: product.teeth,
        teethSelection: this.teethSelection,
      })
    ) {
      completed.push(StepKeysEnum.crownTeeth);
    }

    const hasAbutment = product.items?.some(item => isAbutment(item));

    if (hasAbutment) {
      if (
        product.items?.every(
          item =>
            isImplantCrown(item) ||
            implantCharacteristicsSchema.safeParse(item.implantCharacteristics)
              .success
        )
      ) {
        completed.push(StepKeysEnum.implantConnection);
      }

      if (
        product.items?.every(
          item =>
            isImplantCrown(item) ||
            abutmentCharacteristicsSchema.safeParse(
              item.abutmentCharacteristics
            ).success
        )
      ) {
        completed.push(StepKeysEnum.abutment);
      }
    }

    const hasImplantCrown = product.items?.some(item => isImplantCrown(item));

    if (hasImplantCrown) {
      if (
        product.items?.every(
          item =>
            isAbutment(item) ||
            implantedItemCharacteristicsSchema.safeParse(
              item.implantedItemCharacteristics
            ).success
        )
      ) {
        completed.push(StepKeysEnum.implantRetention);
      }

      if (
        product.items?.every(
          item =>
            isAbutment(item) ||
            materialCharacteristicsSchema.safeParse(
              item.materialCharacteristics
            ).success
        )
      ) {
        completed.push(StepKeysEnum.material);
      }
    }

    if (productPreferencesSchema.safeParse(product).success) {
      completed.push(StepKeysEnum.preferences);
    }

    return completed;
  },
};
